import React, { useState } from 'react';
import { Modal, Rate, Input, Button, Upload, message } from 'antd';
import { CameraOutlined, CloseOutlined } from '@ant-design/icons';
import { Order, LineItem } from '@/infrastructure/domain';
import { useCreateRating } from '@/hooks/rating/useCreateRating';
import { useUploadImage } from '@/hooks/images/useUploadImage';
import Image from '@/components/ui/images/Image';

const { TextArea } = Input;

interface RatingModalProps {
  visible: boolean;
  onClose: () => void;
  order: Order;
}

interface ProductRating {
  lineItem: LineItem;
  rating: number;
  comment: string;
  attachments: string[];
}

const RatingModal: React.FC<RatingModalProps> = ({
  visible,
  onClose,
  order,
}) => {
  const [productRatings, setProductRatings] = useState<ProductRating[]>([]);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);

  const createRating = useCreateRating();
  const uploadImage = useUploadImage();

  const currentProduct = order.lineItems[currentProductIndex];
  const currentRating = productRatings[currentProductIndex] || {
    lineItem: currentProduct,
    rating: 0,
    comment: '',
    attachments: [],
  };

  const handleRatingChange = (value: number) => {
    const updatedRatings = [...productRatings];
    updatedRatings[currentProductIndex] = {
      ...currentRating,
      rating: value,
    };
    setProductRatings(updatedRatings);
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const updatedRatings = [...productRatings];
    updatedRatings[currentProductIndex] = {
      ...currentRating,
      comment: e.target.value,
    };
    setProductRatings(updatedRatings);
  };

  const handleImageUpload = async (file: File) => {
    if (currentRating.attachments.length >= 6) {
      message.error('Maximum 6 photos allowed');
      return false;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source', 'products');

      const response = await uploadImage.mutateAsync(formData);

      const updatedRatings = [...productRatings];
      updatedRatings[currentProductIndex] = {
        ...currentRating,
        attachments: [...currentRating.attachments, response.url],
      };
      setProductRatings(updatedRatings);

      message.success('Photo uploaded successfully');
      return false; // Prevent default upload behavior
    } catch (error) {
      message.error('Failed to upload photo');
      return false;
    }
  };

  const handleRemoveImage = (index: number) => {
    const updatedRatings = [...productRatings];
    updatedRatings[currentProductIndex] = {
      ...currentRating,
      attachments: currentRating.attachments.filter((_, i) => i !== index),
    };
    setProductRatings(updatedRatings);
  };

  const handleNextProduct = () => {
    if (currentProductIndex < order.lineItems.length - 1) {
      setCurrentProductIndex(currentProductIndex + 1);
    }
  };

  const handlePrevProduct = () => {
    if (currentProductIndex > 0) {
      setCurrentProductIndex(currentProductIndex - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      // Create ratings for all products
      const ratingPromises = productRatings.map(productRating => {
        if (productRating.rating === 0) {
          throw new Error(`Please rate ${productRating.lineItem.product.name}`);
        }

        return createRating.mutateAsync({
          marketId: order.marketId,
          productId: productRating.lineItem.productId,
          variantId: productRating.lineItem.variantId,
          rating: productRating.rating,
          comment: productRating.comment,
          attachments: productRating.attachments,
          productSnapshot: {
            quantity: Number(productRating.lineItem.quantity),
            sellingUnit:
              productRating.lineItem.sellingUnit?.name ||
              productRating.lineItem.variant.unit.name,
          },
        });
      });

      await Promise.all(ratingPromises);
      message.success('Thank you for your rating!');
      onClose();

      // Reset state
      setProductRatings([]);
      setCurrentProductIndex(0);
    } catch (error: any) {
      message.error(error.message || 'Failed to submit rating');
    }
  };

  const canSubmit = productRatings.every(rating => rating.rating > 0);
  const isLastProduct = currentProductIndex === order.lineItems.length - 1;

  return (
    <Modal
      title="Rate"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={500}
      closeIcon={<CloseOutlined />}
      className="rating-modal"
    >
      <div className="space-y-6">
        {/* Product Info */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <Image
            imageSource={currentProduct.product.images[0]}
            className="w-12 h-12 rounded-lg object-cover"
          />
          <div>
            <h3 className="font-semibold text-gray-900">
              {currentProduct.product.name}
            </h3>
            <p className="text-sm text-gray-500">
              x{currentProduct.quantity}{' '}
              {currentProduct.sellingUnit?.name ||
                currentProduct.variant.unit.name}
            </p>
          </div>
        </div>

        {/* Product Navigation (if multiple products) */}
        {order.lineItems.length > 1 && (
          <div className="flex justify-between items-center">
            <Button
              onClick={handlePrevProduct}
              disabled={currentProductIndex === 0}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-500">
              {currentProductIndex + 1} of {order.lineItems.length}
            </span>
            <Button onClick={handleNextProduct} disabled={isLastProduct}>
              Next
            </Button>
          </div>
        )}

        {/* Rate Product */}
        <div>
          <h4 className="font-semibold mb-3">Rate Product</h4>
          <Rate
            value={currentRating.rating}
            onChange={handleRatingChange}
            className="text-2xl"
          />
        </div>

        {/* Add Photo */}
        <div>
          <h4 className="font-semibold mb-3">Add Photo</h4>
          <div className="flex gap-3">
            <Upload
              accept="image/*"
              beforeUpload={handleImageUpload}
              showUploadList={false}
            >
              <Button
                icon={<CameraOutlined />}
                className="flex flex-col items-center justify-center h-20 w-20 border-dashed"
              >
                <div className="text-xs">Add Photo</div>
                <div className="text-xs text-gray-400">
                  {currentRating.attachments.length}/6
                </div>
              </Button>
            </Upload>

            {currentRating.attachments.map((url, index) => (
              <div key={index} className="relative">
                <Image
                  imageSource={url}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  size="small"
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 p-0"
                  onClick={() => handleRemoveImage(index)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Leave Review */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold">Leave Review</h4>
            <span className="text-sm text-gray-500">
              {currentRating.comment.length}/100 characters
            </span>
          </div>
          <TextArea
            value={currentRating.comment}
            onChange={handleCommentChange}
            placeholder="Share your thoughts about the product."
            maxLength={100}
            rows={4}
            className="rounded-lg"
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || createRating.isPending}
            loading={createRating.isPending}
            className="px-8"
          >
            Submit
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RatingModal;
